<template>
  <div class="ap-page">
    <div class="ap-header">
      <h1 class="ap-title">活動公告管理</h1>
      <button class="ap-btn ap-btn--primary" @click="openAdd">＋ 新增公告</button>
    </div>

    <p class="ap-hint">活動公告會以橫幅方式顯示在商品列表頁與首頁，適合放置「買三送一」等促銷訊息。</p>

    <div v-if="loading" class="ap-state">載入中…</div>

    <div v-else-if="promotions.length === 0" class="ap-state">
      尚無公告，點右上角「＋ 新增公告」建立第一則。
    </div>

    <table v-else class="ap-table">
      <thead>
        <tr>
          <th>排序</th>
          <th>公告內容</th>
          <th>狀態</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in promotions" :key="p.id">
          <td class="ap-sort">{{ p.sort_order }}</td>
          <td class="ap-content">{{ p.content }}</td>
          <td>
            <span class="ap-badge" :class="p.is_active ? 'ap-badge--on' : 'ap-badge--off'">
              {{ p.is_active ? '啟用' : '停用' }}
            </span>
          </td>
          <td class="ap-actions">
            <button class="ap-btn ap-btn--sm ap-btn--secondary" @click="openEdit(p)">編輯</button>
            <button class="ap-btn ap-btn--sm ap-btn--toggle" @click="toggleActive(p)">
              {{ p.is_active ? '停用' : '啟用' }}
            </button>
            <button class="ap-btn ap-btn--sm ap-btn--danger" @click="deletePromotion(p)">刪除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal -->
    <teleport to="body">
      <div v-if="showModal" class="ap-overlay" @click.self="closeModal">
        <div class="ap-modal">
          <div class="ap-modal__header">
            <h2>{{ editingId ? '編輯公告' : '新增公告' }}</h2>
            <button class="ap-modal__close" @click="closeModal">✕</button>
          </div>
          <div class="ap-modal__body">
            <div class="ap-field">
              <label>公告內容 <span class="ap-req">*</span>
                <span class="ap-hint-inline">（最多 200 字，例：買三送一，多件更優惠！）</span>
              </label>
              <input v-model="form.content" type="text" maxlength="200" class="ap-input"
                placeholder="例：買三送一、消費滿千免運" />
              <div class="ap-charcount">{{ form.content.length }} / 200</div>
              <p v-if="errors.content" class="ap-err">{{ errors.content }}</p>
            </div>
            <div class="ap-field ap-field--row">
              <label>排序數字
                <span class="ap-hint-inline">（數字小排前面，預設 0）</span>
              </label>
              <input v-model.number="form.sort_order" type="number" min="0" class="ap-input ap-input--sm"
                placeholder="0" />
            </div>
            <div class="ap-field">
              <label>
                <input type="checkbox" v-model="form.is_active" style="margin-right:6px" />
                立即啟用
              </label>
            </div>
            <!-- 預覽 -->
            <div v-if="form.content.trim()" class="ap-preview">
              <span class="ap-preview__label">前台預覽：</span>
              <span class="ap-preview__tag">📣 {{ form.content }}</span>
            </div>
            <p v-if="apiError" class="ap-err">{{ apiError }}</p>
          </div>
          <div class="ap-modal__footer">
            <button class="ap-btn ap-btn--secondary" @click="closeModal">取消</button>
            <button class="ap-btn ap-btn--primary" :disabled="submitting" @click="save">
              {{ submitting ? '儲存中…' : '儲存' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'AdminPromotions',
  data() {
    return {
      promotions: [],
      loading: false,
      showModal: false,
      editingId: null,
      form: { content: '', sort_order: 0, is_active: true },
      errors: {},
      apiError: '',
      submitting: false,
    };
  },
  async created() {
    await this.fetchPromotions();
  },
  methods: {
    async fetchPromotions() {
      this.loading = true;
      try {
        const res = await axios.get('/api/admin/promotions');
        if (res.data.success) this.promotions = res.data.promotions;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editingId = null;
      this.form = { content: '', sort_order: 0, is_active: true };
      this.errors = {};
      this.apiError = '';
      this.showModal = true;
    },
    openEdit(p) {
      this.editingId = p.id;
      this.form = { content: p.content, sort_order: p.sort_order, is_active: !!p.is_active };
      this.errors = {};
      this.apiError = '';
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    validate() {
      const e = {};
      if (!this.form.content.trim()) e.content = '請填寫公告內容';
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    async save() {
      if (!this.validate()) return;
      this.submitting = true;
      this.apiError = '';
      try {
        const payload = { ...this.form };
        const res = this.editingId
          ? await axios.put(`/api/admin/promotions/${this.editingId}`, payload)
          : await axios.post('/api/admin/promotions', payload);
        if (res.data.success) {
          this.closeModal();
          await this.fetchPromotions();
        } else {
          this.apiError = res.data.message || '儲存失敗';
        }
      } catch (e) {
        this.apiError = e.response?.data?.message || '伺服器錯誤';
      } finally {
        this.submitting = false;
      }
    },
    async toggleActive(p) {
      try {
        await axios.put(`/api/admin/promotions/${p.id}`, {
          content: p.content,
          sort_order: p.sort_order,
          is_active: p.is_active ? 0 : 1,
        });
        await this.fetchPromotions();
      } catch (e) {
        alert('操作失敗');
      }
    },
    async deletePromotion(p) {
      const result = await Swal.fire({
        title: `確定刪除「${p.content}」嗎？`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定刪除',
        cancelButtonText: '取消',
        confirmButtonColor: '#e74c3c',
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/api/admin/promotions/${p.id}`);
        await this.fetchPromotions();
      } catch (e) {
        alert('刪除失敗');
      }
    },
  },
};
</script>

<style scoped>
.ap-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 20px;
}
.ap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 12px;
}
.ap-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}
.ap-hint {
  font-size: 0.88rem;
  color: #7f8c8d;
  margin: 0 0 24px;
}
.ap-hint-inline {
  font-size: 0.78rem;
  color: #999;
  font-weight: 400;
}
.ap-state {
  text-align: center;
  color: #7f8c8d;
  padding: 40px 0;
  font-size: 0.95rem;
}
.ap-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}
.ap-table th,
.ap-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #eaeaea;
  text-align: left;
  vertical-align: middle;
}
.ap-table thead th {
  background: #f4f6f8;
  font-weight: 600;
  color: #555;
}
.ap-sort { color: #888; width: 60px; text-align: center; }
.ap-content { max-width: 400px; word-break: break-all; }
.ap-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
}
.ap-badge--on  { background: #d4edda; color: #155724; }
.ap-badge--off { background: #e9ecef; color: #6c757d; }
.ap-actions { display: flex; gap: 6px; flex-wrap: wrap; }

.ap-btn {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 600;
  transition: opacity .2s;
}
.ap-btn:disabled { opacity: .6; cursor: not-allowed; }
.ap-btn--primary   { background: #2E3A59; color: #fff; }
.ap-btn--primary:hover { opacity: .85; }
.ap-btn--secondary { background: #e9ecef; color: #495057; }
.ap-btn--secondary:hover { background: #dee2e6; }
.ap-btn--toggle { background: #fff3cd; color: #856404; }
.ap-btn--toggle:hover { background: #ffeeba; }
.ap-btn--danger  { background: #f8d7da; color: #721c24; }
.ap-btn--danger:hover { background: #f5c6cb; }
.ap-btn--sm { padding: 5px 12px; font-size: 0.82rem; }

/* Modal */
.ap-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 2000;
  display: flex; align-items: center; justify-content: center;
}
.ap-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 8px 40px rgba(0,0,0,.18);
  overflow: hidden;
}
.ap-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  background: #f4f6f8;
  border-bottom: 1px solid #eaeaea;
}
.ap-modal__header h2 { margin: 0; font-size: 1.1rem; color: #2c3e50; }
.ap-modal__close {
  background: none; border: none; font-size: 1.1rem;
  cursor: pointer; color: #7f8c8d;
}
.ap-modal__body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.ap-modal__footer {
  padding: 14px 20px;
  display: flex; justify-content: flex-end; gap: 10px;
  border-top: 1px solid #eaeaea;
}
.ap-field { display: flex; flex-direction: column; gap: 6px; }
.ap-field--row { flex-direction: row; align-items: center; gap: 12px; }
.ap-field label { font-size: 0.88rem; font-weight: 600; color: #444; }
.ap-req { color: #e74c3c; }
.ap-input {
  padding: 9px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  transition: border .2s;
  width: 100%;
  box-sizing: border-box;
}
.ap-input--sm { width: 80px; }
.ap-input:focus { border-color: #2E3A59; }
.ap-charcount { font-size: 0.75rem; color: #aaa; text-align: right; }
.ap-err { color: #e74c3c; font-size: 0.82rem; margin: 0; }
.ap-preview {
  background: #fff8f0;
  border: 1px dashed #f0a040;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ap-preview__label { font-size: 0.8rem; color: #999; white-space: nowrap; }
.ap-preview__tag { font-size: 0.9rem; color: #cc4400; font-weight: 600; }
</style>
